<template>
  <div>
    <AdminNav />
    <div class="page">
      <h2 class="page-title">Requêtes SQL</h2>

      <div class="card">
        <div class="warning-box">
          ⚠️ Seules les requêtes <strong>SELECT</strong> sont autorisées. Aucune modification de données possible.
        </div>

        <!-- Requêtes rapides -->
        <div class="quick-queries">
          <h4>Requêtes rapides</h4>
          <div class="quick-btns">
            <button class="quick-btn" @click="setQuery('SELECT * FROM commercants ORDER BY created_at DESC')">Tous les commerçants</button>
            <button class="quick-btn" @click="setQuery('SELECT * FROM clients WHERE actif = true ORDER BY points_solde DESC LIMIT 20')">Top clients par points</button>
            <button class="quick-btn" @click="setQuery('SELECT date, ca_fidelise, ca_total_saisi, statut FROM journees ORDER BY date DESC LIMIT 30')">Dernières journées</button>
            <button class="quick-btn" @click="setQuery('SELECT COUNT(*), SUM(montant_achat), AVG(montant_achat) FROM transactions')">Stats transactions</button>
            <button class="quick-btn" @click="setQuery('SELECT categorie, SUM(montant) as total FROM depenses GROUP BY categorie ORDER BY total DESC')">Dépenses par catégorie</button>
            <button class="quick-btn" @click="setQuery('SELECT c.prenom, c.nom, c.points_solde, COUNT(t.id) as nb_achats FROM clients c LEFT JOIN transactions t ON t.client_id = c.id WHERE c.actif = true GROUP BY c.id ORDER BY nb_achats DESC LIMIT 20')">Clients les plus actifs</button>
          </div>
        </div>

        <!-- Éditeur SQL -->
        <div class="sql-editor">
          <label class="label">Requête SQL</label>
          <textarea
            v-model="query"
            class="sql-textarea"
            placeholder="SELECT * FROM clients LIMIT 10"
            rows="5"
            @keydown.ctrl.enter="executer"
          ></textarea>
          <p class="hint">Ctrl+Entrée pour exécuter</p>
        </div>

        <div class="editor-actions">
          <button class="btn btn-primary" @click="executer" :disabled="loading" style="width:auto;padding:10px 24px">
            {{ loading ? 'Exécution...' : '▶ Exécuter' }}
          </button>
          <button class="btn btn-secondary" @click="query = ''" style="width:auto;padding:10px 20px">Effacer</button>
        </div>

        <p v-if="erreur" class="error">{{ erreur }}</p>
      </div>

      <!-- Résultats -->
      <div class="card resultats-card" v-if="resultats">
        <div class="resultats-header">
          <h3>Résultats</h3>
          <span class="count-badge">{{ resultats.count }} ligne(s)</span>
          <button class="btn-export" @click="exportCSV">⬇️ CSV</button>
        </div>

        <div class="table-wrapper" v-if="resultats.rows.length > 0">
          <table class="table">
            <thead>
              <tr>
                <th v-for="col in colonnes" :key="col">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in resultats.rows" :key="i">
                <td v-for="col in colonnes" :key="col">{{ row[col] ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="vide" v-else>Aucun résultat</div>
      </div>

    </div>
  </div>
</template>

<script>
import AdminNav from '../../components/AdminNav.vue'
import adminApi from '../../adminApi'

export default {
  name: 'AdminSQL',
  components: { AdminNav },
  data() { return { query: '', resultats: null, erreur: '', loading: false } },
  computed: {
    colonnes() {
      if (!this.resultats?.rows?.length) return []
      return Object.keys(this.resultats.rows[0])
    }
  },
  methods: {
    setQuery(q) { this.query = q },
    async executer() {
      this.erreur   = ''
      this.resultats = null
      this.loading  = true
      try {
        const res = await adminApi.post('/sql', { query: this.query })
        this.resultats = res.data
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Erreur d\'exécution'
      } finally { this.loading = false }
    },
    exportCSV() {
      if (!this.resultats?.rows?.length) return
      const cols = this.colonnes
      const lines = [cols.join(';')]
      this.resultats.rows.forEach(row => {
        lines.push(cols.map(c => `"${row[c] ?? ''}"`).join(';'))
      })
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href = url; a.download = 'export.csv'; a.click()
    }
  }
}
</script>

<style scoped>
.page { width: 100%; max-width: 1280px; margin: 0 auto; padding: 24px; box-sizing: border-box; }
.page-title { font-size: 22px; font-weight: 700; margin-bottom: 20px; }
.card { background: white; border-radius: 14px; padding: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); margin-bottom: 16px; }

.warning-box { background: #fffbeb; border: 1.5px solid #f6ad55; border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #744210; margin-bottom: 16px; }

.quick-queries h4 { font-size: 14px; font-weight: 600; color: #4a5568; margin-bottom: 10px; }
.quick-btns { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
.quick-btn { padding: 7px 14px; border-radius: 8px; border: 1.5px solid #e2e8f0; background: white; font-size: 12px; cursor: pointer; color: #4a5568; transition: all 0.2s; }
.quick-btn:hover { border-color: #2d3748; color: #2d3748; background: #f7fafc; }

.label { display: block; font-size: 13px; font-weight: 600; color: #4a5568; margin-bottom: 6px; }
.sql-textarea { width: 100%; padding: 12px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-family: monospace; font-size: 13px; line-height: 1.6; resize: vertical; outline: none; }
.sql-textarea:focus { border-color: #2d3748; }
.hint { font-size: 11px; color: #a0aec0; margin-top: 4px; }
.editor-actions { display: flex; gap: 10px; margin-top: 12px; }
.btn { padding: 10px 20px; border-radius: 10px; border: none; font-size: 14px; font-weight: 600; cursor: pointer; }
.btn-primary { background: #2d3748; color: white; }
.btn-primary:disabled { background: #a0aec0; cursor: not-allowed; }
.btn-secondary { background: #edf2f7; color: #4a5568; }
.error { color: #e53e3e; font-size: 13px; margin-top: 10px; }

.resultats-header { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.resultats-header h3 { font-size: 16px; font-weight: 700; flex: 1; }
.count-badge { background: #edf2f7; color: #4a5568; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
.btn-export { background: none; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 6px 12px; cursor: pointer; font-size: 13px; }
.btn-export:hover { background: #f7fafc; }

.table-wrapper { overflow-x: auto; }
.table { width: 100%; border-collapse: collapse; font-size: 13px; }
.table th { text-align: left; padding: 8px 12px; font-size: 11px; color: #718096; border-bottom: 2px solid #e2e8f0; white-space: nowrap; background: #f7fafc; }
.table td { padding: 8px 12px; border-bottom: 1px solid #f0f0f0; white-space: nowrap; max-width: 300px; overflow: hidden; text-overflow: ellipsis; }
.table tr:hover td { background: #f7fafc; }
.vide { text-align: center; padding: 30px; color: #a0aec0; }
@media (max-width: 600px) { .page { padding: 16px; } }
</style>
